import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  EventStaffPrimary,
  TEventsApiResponse,
  TEventsPrimary,
} from '../../../../apis/events/events.interfaces';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import {
  useStudentsEventsHook,
  TStudentMonthWindowParams,
} from '../hooks/useStudentsEventsHook';
import { TStudentDetailsSimplified } from '../../../../apis/students/students.interfaces';
import { useStudentFiltersState } from '../contexts/studentFilters.context';
import { useEventsListHook } from '../../events/hooks/useEventsListHook';
import { SECTION } from '../../../../apis/admissions/admissions.interfaces';
import { GRADE } from '../../../../apis/admissions/admissions.interfaces';
import { GENDER } from '../../../../apis/users/users.interfaces';
import { EventGroup, EventScope } from '../../../../apis/events/event.enums';

type TStudentEventsAppState = {
  errorMessage?: string;
  isLoading: boolean;
  requestParams: TPaginationParams;
  eventsList: TEventsPrimary[];
  currentMonth: string; // YYYY-MM format
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_EVENTS = 'UPDATE_EVENTS',
  UPDATE_EVENTS_LIST = 'UPDATE_EVENTS_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_CURRENT_MONTH = 'SET_CURRENT_MONTH',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.UPDATE_EVENTS; payload: TEventsPrimary[] }
  | { type: ACTION_TYPE.UPDATE_EVENTS_LIST; payload: TEventsPrimary[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_CURRENT_MONTH; payload: string };

const initialStudentEventsState: TStudentEventsAppState = {
  eventsList: [],
  errorMessage: undefined,
  isLoading: false,
  currentMonth: new Date().toISOString().slice(0, 7),
  requestParams: {
    page: 1,
    limit: 10,
  },
};

type TState = {
  studentEventsAppState: TStudentEventsAppState;
  studentEventsResponse: TEventsApiResponse;
  simplifiedStudentObject: Record<string, TStudentDetailsSimplified>;
  filteredEvents: TEventsPrimary[];
};

const StudentEventsContext = createContext<TState | undefined>(undefined);
const StudentEventsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const StudentEventsReducer = (
  state: TStudentEventsAppState,
  action: Action
): TStudentEventsAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        requestParams: { ...state.requestParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_EVENTS:
      return { ...state, ...action.payload };
    case ACTION_TYPE.UPDATE_EVENTS_LIST:
      return { ...state, eventsList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_CURRENT_MONTH:
      return { ...state, currentMonth: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StudentEventsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [studentEventsAppState, dispatch] = useReducer(
    StudentEventsReducer,
    initialStudentEventsState
  );
  const filters = useStudentFiltersState();
  const simplifiedStudentObject = useMemo<
    Record<string, TStudentDetailsSimplified>
  >(
    () => ({
      '9027555614': {
        // Grade 11-C student
        id: '9027555614',
        grade: GRADE.ELEVEN,
        section: SECTION.C,
        basicDetails: {
          firstName: 'Test',
          lastName: 'User',
          gender: GENDER.MALE,
        },
      },
      '123456789': {
        // Grade 8-A student
        id: '123456789',
        grade: GRADE.EIGHT,
        section: SECTION.A,
        basicDetails: {
          firstName: 'John',
          lastName: 'Smith',
          gender: GENDER.MALE,
        },
      },
      '987654321': {
        // Grade 8-B student
        id: '987654321',
        grade: GRADE.EIGHT,
        section: SECTION.B,
        basicDetails: {
          firstName: 'Jane',
          lastName: 'Doe',
          gender: GENDER.FEMALE,
        },
      },
    }),
    []
  );

  // Month-window params — instituteId is now extracted from JWT on the backend
  const monthWindowParams: TStudentMonthWindowParams = useMemo(() => ({
    ...studentEventsAppState.requestParams,
    currentMonth: studentEventsAppState.currentMonth,
    includeSpanning: true,
    mode: 'window',
  }), [studentEventsAppState.requestParams, studentEventsAppState.currentMonth]);

  const {
    data: studentEventsResponse,
    isLoading: studentEventsResponseLoading,
    isError: studentEventsResponseLoadingError,
    isSuccess: studentEventsResponseLoadingDataSuccess,
    error: studentEventsResponseDataError,
  } = useStudentsEventsHook(monthWindowParams);

  useEffect(() => {
    if (studentEventsResponseLoading) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (studentEventsResponseLoadingDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (studentEventsResponseLoadingError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          studentEventsResponseDataError?.response?.data.message ??
          ERROR_MESSAGE.GET_EVENTS_FAILURE,
      });
    }

    return () => {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    };
  }, [
    studentEventsResponseLoading,
    studentEventsResponseLoadingDataSuccess,
    studentEventsResponseLoadingError,
    studentEventsResponseDataError,
  ]);

  const { data: eventsListData } = useEventsListHook({
    page: 1,
    limit: 100,
  });

  const filteredEvents = useMemo<TEventsPrimary[]>(() => {
    const sourceEvents =
      Array.isArray(studentEventsResponse?.data) &&
      studentEventsResponse?.data.length
        ? studentEventsResponse.data
        : eventsListData?.data || [];

    const mappedEvents = sourceEvents.map((event: any) => {
      // If event already has eventScopes (from API normalization), use it
      // Otherwise normalize from eventScope (for mock data)
      const normalizedScopes =
        event.eventScopes &&
        Array.isArray(event.eventScopes) &&
        event.eventScopes.length > 0
          ? event.eventScopes
          : Array.isArray(event.eventScope)
            ? event.eventScope
                .flat(Infinity)
                .filter((s: any) => s && typeof s === 'string')
            : [event.eventScope].filter(Boolean);

      return {
        id: event.id,
        title: event.title,
        manager: event.manager,
        role: 'participant',
        description: event.description,
        startDate: event.startDate,
        endDate: event.endDate,
        eventGroup: event.eventGroup as EventGroup,
        eventScope: (normalizedScopes[0] ||
          event.eventScope ||
          'institute') as EventScope,
        eventScopes: normalizedScopes as EventScope[],
        expenditureActual: event.expenditureActual,
        expenditureEstimate: event.expenditureEstimate,
        institute: event.institute,
        plannedBy: event.plannedBy,
        participants: (event.participants || []).map((p: any) => ({
          type: 'student' as const,
          id: p.id,
          isFeeRequired: false,
          status: 'confirmed' as const,
          attendanceStatus: 'present' as const,
        })),
        staffAssignments: (event.staffAssignments || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          email: s.email,
          eventRole: s.eventRole,
        })) as EventStaffPrimary[],
        purpose: event.purpose,
        remarks: event.remarks,
        venue: event.venue,
        priority: 'medium' as const,
        isMandatory: true,
        status: (event.status === 'scheduled' ? 'scheduled' : 'completed') as
          | 'scheduled'
          | 'completed'
          | 'cancelled',
        relatedClass: {
          grade: '11' as GRADE,
          section: 'C' as SECTION,
        },
      };
    });

    const filtered = mappedEvents.filter((event) => {
      // Require 'student' in scopes
      const scopes =
        event.eventScopes && event.eventScopes.length > 0
          ? event.eventScopes
          : [event.eventScope].filter(Boolean);

      const hasStudentScope = scopes.some(
        (scope: any) =>
          scope &&
          typeof scope === 'string' &&
          scope.toLowerCase() === 'student'
      );

      if (!hasStudentScope) {
        return false;
      }

      // Optional: filter by participant if available
      const studentId = event.participants?.[0]?.id;
      if (!studentId) {
        // No participant specified - include the event (general student event)
        return true;
      }

      const student = simplifiedStudentObject[studentId];
      if (!student) {
        // Participant not in our list - still include the event
        return true;
      }

      // If we have a matching student, apply grade/section filters
      const gradeMatch =
        !filters.grade ||
        filters.grade === 'select' ||
        student.grade === filters.grade;

      const sectionMatch =
        !filters.section ||
        filters.section === 'select' ||
        student.section === filters.section;

      return gradeMatch && sectionMatch;
    });

    return filtered;
  }, [filters, simplifiedStudentObject, studentEventsResponse, eventsListData]);

  const currentState = useMemo<TState>(
    () => ({
      studentEventsAppState,
      studentEventsResponse: studentEventsResponse ?? { data: [], total: 0, page: 1, limit: 10, totalPages: 0 },
      simplifiedStudentObject,
      filteredEvents,
    }),
    [
      studentEventsAppState,
      studentEventsResponse,
      filteredEvents,
      simplifiedStudentObject,
    ]
  );

  return (
    <StudentEventsDispatchContext.Provider value={dispatch}>
      <StudentEventsContext.Provider value={currentState}>
        {children}
      </StudentEventsContext.Provider>
    </StudentEventsDispatchContext.Provider>
  );
};

export const useStudentEventsState = () => {
  const state = useContext(StudentEventsContext);
  if (state === undefined) {
    throw new Error(
      'useStudentEventsState must be used within a StudentEventsProvider'
    );
  }
  return state;
};

export const useStudentEventsActions = () => {
  const dispatch = useContext(StudentEventsDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStudentEventsActions dispatch must be used within a StudentEventsProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const updateEventsList = (list: TEventsPrimary[]) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_EVENTS_LIST,
      payload: list,
    });
  };

  const setCurrentMonth = (month: string) => {
    dispatch({ type: ACTION_TYPE.SET_CURRENT_MONTH, payload: month });
  };

  return { updateSearchParams, updateEventsList, setCurrentMonth };
};
